import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  DirectAnswer,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { anchorFor, breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

const PATH = "/fractional-cto-southeast-asia";
const CTA_SOURCE_HERO = "fractional_cto_sea_hero";
const CTA_SOURCE_BAND = "fractional_cto_sea_footer";

const title = "Fractional CTO for South East Asia | Timothy Ng";
const description =
  "Delivery slipping across time zones? Fractional CTO for South East Asia scale-ups, working remote-first with founders and boards in the region.";

const FAQ = [
  {
    q: "Do you work with companies based in South East Asia?",
    a: "Yes. Engagements run remote by default, with fixed weekly contact and board attendance by video. I also travel to the region occasionally and work over there where a board or team needs it, arranged and costed per engagement.",
  },
  {
    q: "How does the time difference work?",
    a: "Malaysia, Singapore and Hong Kong all run GMT+8 with no daylight saving, so the gap from the UK is seven hours in British Summer Time and eight in winter. That gives a dependable late-afternoon slot on your side every working day, a 09:00 UK call is 16:00 for you in summer, 17:00 in winter, and I start earlier by arrangement where a team needs a longer window. Everything else runs on written reporting rather than a call at an unreasonable hour for one of us.",
  },
  {
    q: "Do you cover regional data protection obligations?",
    a: "I cover them as an engineering and governance problem, not as legal advice: what the systems actually do with personal data, and whether that stands up. The regimes in play are Hong Kong's Personal Data (Privacy) Ordinance, Cap. 486, overseen by the PCPD; Malaysia's Personal Data Protection Act 2010 (Act 709), overseen by JPDP; and Singapore's Personal Data Protection Act 2012, overseen by the PDPC. Where a question is genuinely legal, you want local counsel, and I will say so.",
  },

  {
    q: "Which countries do you cover?",
    a: "Malaysia and Singapore are the primary focus, with Indonesia, Vietnam and Thailand covered on the same remote-first basis. I am UK-based, work to your hours, and travel over occasionally to work on the ground.",
  },
  {
    q: "What sectors do you know?",
    a: "Fintech, iGaming and gambling, digital health, SaaS, data and agencies. Those are sectors I have run technology and operations in, not sectors I have read about.",
  },
  {
    q: "How is this different from a non-executive director role?",
    a: "A fractional CTO works inside the business, owning technical decisions and leading the team. A non-executive director sits outside it, holding the executive to account and giving the board independent assurance. I keep the two separate and will say which one you actually need.",
  },
];

const FIT = [
  {
    title: "Pre-Series A founders",
    body: "Technical decisions are already executive-level, but the headcount is not. You need senior judgement on architecture, hiring and the outsourced team, at a cadence you can afford.",
  },
  {
    title: "Series A and beyond",
    body: "The board needs independent technical assurance: an unfiltered read on delivery, spend, security and the next twelve months, written so it can be acted on.",
  },
];

const WORKING = [
  {
    title: "Remote by default",
    body: "Fixed weekly contact, board attendance by video, and written reports your team and your investors can both act on.",
  },
  {
    title: "On-site when it earns its place",
    body: "Some things need a room: a board offsite, a team reset, a due diligence week. On-site time is arranged and costed per engagement.",
  },
  {
    title: "A stated overlap window, not a vague promise",
    body: "GMT+8 with no daylight saving means the gap is seven hours in UK summer and eight in winter. A 09:00 UK start is 16:00 or 17:00 your time, every working day, with earlier starts by arrangement when a delivery push needs them.",
  },
];

const LOCATIONS = [
  {
    to: "/fractional-cto-malaysia",
    name: "Malaysia",
    body: "Country-wide cover on MYT (GMT+8), with on-site time in KL, Penang or Johor arranged per engagement.",
  },
  {
    to: "/fractional-cto-kuala-lumpur",
    name: "Kuala Lumpur",
    body: "The KL and Klang Valley page: delivery cadence, costs and how the overlap window works day to day.",
  },
  {
    to: "/fractional-cto-hong-kong",
    name: "Hong Kong",
    body: "Hong Kong founders and boards on HKT (GMT+8), remote-first with travel agreed up front.",
  },
] as const;

export const Route = createFileRoute("/fractional-cto-southeast-asia")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "fractional-cto-southeast-asia",
        "Fractional CTO for South East Asia, GMT+8 aligned | Timothy Ng",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO for South East Asia | Timothy Ng, Scryla",
          areaServed: AREAS.southeastAsia,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.southeastAsia,
        }),
      ),
      breadcrumbMeta(PATH),
    ],
  }),
  component: SeaPage,
});

function SeaPage() {
  return (
    <div>
      <section
        aria-labelledby="sea-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Fractional CTO · South East Asia</Eyebrow>
          <h1
            id="sea-hero-heading"
            className="mt-4 max-w-[20ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO for South East Asia scale-ups.
          </h1>
          <DirectAnswer className="mt-6">
            A fractional CTO is an experienced chief technology officer engaged part-time rather
            than as a full-time hire. Across South East Asia that means senior technical judgement
            delivered remote-first on GMT+8 hours, with in-person time arranged and costed per
            engagement rather than assumed.
          </DirectAnswer>
          <Lead className="mt-5">
            Delivery running late across time zones with nobody senior owning it? Former CTO and
            COO, and founder of my own consultancy since 2014. I work with founders and boards in
            Malaysia, Singapore and the wider region who need senior technical judgement without a
            full-time executive hire.
          </Lead>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="sea-region-heading">
        <Container>
          <h2
            id="sea-region-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Where I work in the region
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            <p>
              Malaysia and Singapore first, with Indonesia, Vietnam and Thailand covered on the same
              basis. I have advised clients in the region remotely, working to GMT+8 hours rather
              than expecting them to take a call at UK convenience. I am based in {SITE.location},
              so the work runs remote-first, and I travel to the region occasionally to work on the
              ground where being there changes the outcome. I have family in Hong Kong and a long
              connection to the region, so the culture and the way business runs there are familiar
              rather than something I have read up on.
            </p>

            <p>
              Sectors I have actually run technology and operations in: fintech, iGaming and
              gambling, digital health, SaaS, data and agencies. I take on a small number of clients
              at a time, have no development team to keep busy and no supplier referrals to protect.
            </p>
          </div>
          {/* Market links live once, in the "Pages for individual markets"
              section below, so each city is linked with one canonical anchor. */}

          <RelatedLinks className="mt-7" paths={["/who-i-am"]} />
        </Container>
      </section>

      <section
        className="border-b border-border bg-surface"
        aria-labelledby="sea-locations-heading"
      >
        <Container>
          <Eyebrow>By location</Eyebrow>
          <h2
            id="sea-locations-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Pages for individual markets
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {LOCATIONS.map((item) => (
              <li key={item.to} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">
                  <TextLink to={item.to}>{anchorFor(item.to)}</TextLink>
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="sea-working-heading">
        <Container>
          <Eyebrow>How the engagement works</Eyebrow>
          <h2
            id="sea-working-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Working with clients outside the UK
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {WORKING.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-[62ch] text-[15px] leading-relaxed text-body">
            No lock-in, and no interest in staying longer than the business needs me. When you are
            ready to bring the role in-house, I help you hire well and step back.
          </p>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="sea-fit-heading">
        <Container>
          <Eyebrow>Where I fit</Eyebrow>
          <h2
            id="sea-fit-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Two different problems
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {FIT.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <RelatedLinks className="mt-7" paths={["/fractional-cto-vs-full-time-vs-agency"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="sea-quotes-heading">
        <Container>
          <h2 id="sea-quotes-heading" className="sr-only">
            What founders say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.milanShah} />
            <Quote item={TESTIMONIALS.danielReilly} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="sea-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="sea-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            South East Asia FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-8"
            label="Other regions"
            variant="cards"
            paths={["/fractional-cto-uk", "/fractional-cto-us"]}
          />
        </Container>
      </section>

      <CtaBand
        heading="Building in South East Asia?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
